import { getStock } from '@cart-services/core/couponService'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowRightSmall } from '@pets-ui/icons'
import { Image } from '@pets-ui/Image/Image'
import { Select, type SelectProps } from '@pets-ui/Select/Select'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { Tooltip } from '@pets-ui/Tooltip/Tooltip'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack, type VStackProps } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { useTranslation } from 'react-i18next'
import { CouponCondition, type CouponConditionProps } from '../CouponCondition/CouponCondition'
import { CouponConditionLabel } from '../CouponCondition/CouponConditionLabel'
import { CouponConditionPrefixIcon } from '../CouponCondition/CouponConditionPrefixIcon'
import { CouponConditionPrefixText } from '../CouponCondition/CouponConditionPrefixText'
import cartUiI18nInstance from '../i18n/i18nConfig'

const styles = stylex.create({
  tooltip: {
    padding: '8px 12px',
    maxWidth: 320,
  },
})
export interface RegularGiveawaySelectProps extends Omit<SelectProps<[
  ECommerceProduct['id'],
  ECommerceProduct['project_code'],
  string, // Note: 這是 sku，不使用 ECommerceProduct['sku'] 因為他是 nullable
] | []>, 'children' | 'rootRef'> {
  variant?: 'default' | 'progress'
  rootRef?: VStackProps['rootRef']
  options?: ECommerceProduct[]
  amount?: number
  currency?: string
  isAllUnavailable?: boolean
  errorHighlight?: boolean
  shouldHideProgressLine?: boolean
  displayName?: CouponConditionProps['displayName']
  isApplied?: CouponConditionProps['isApplied']
  moreAmount?: CouponConditionProps['moreAmount']
  moreQuantity?: CouponConditionProps['moreQuantity']
  count?: CouponConditionProps['count']
}

const GIFT_ICON_URL = 'https://cdn-v2.dogcatstar.com/common/images/cart/img_gift2.png'

export function RegularGiveawaySelect(props: RegularGiveawaySelectProps) {
  const {
    variant = 'default',
    rootRef,
    options = [],
    amount = 0,
    currency,
    isAllUnavailable,
    errorHighlight,
    displayName,
    isApplied,
    moreAmount,
    moreQuantity,
    count,
    value,
    shouldHideProgressLine = false,
    ...rest
  } = props
  const selectedOption = options.find(option =>
    value
    && option.id === value[0]
    && option.project_code === value[1]
    && option.sku === value[2],
  )
  const isProgress = variant === 'progress'
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const isMobile = useResponsive('mobile')

  const renderSelectItem = () => (
    <HStack gap={2} alignItems="flex-start" flexWrap="nowrap">
      <Box width={36} height={36} borderRadius={4} overflow="hidden" flexShrink={0}>
        <Image
          src={selectedOption?.thumbnail ? selectedOption.thumbnail : undefined}
          alt={selectedOption?.sku ? selectedOption.sku : undefined}
          fallback={GIFT_ICON_URL}
          width="100%"
        />
      </Box>
      <Box flexGrow={1}>
        <Select
          {...rest}
          value={value}
          placeholder={
            isAllUnavailable
              ? `(${t('cartUi.giftsOutOfStock', { defaultValue: '贈品已贈完' })})`
              : t('cartUi.selectGiftsWithPurchase', { amount: formatPrice(currency, amount), defaultValue: `請選擇 ${formatPrice(currency, amount)} 滿額禮` })
          }
          fullWidth
        >
          {options.map((option) => {
            const stock = getStock(option.stock, option.is_out_of_stock, option.manage_stock)
            return (
              <SelectOption
                key={option.id}
                value={[option.id, option.project_code, option.sku ?? '']}
                disabled={stock <= 0}
                data-testid="select-regular-giveaway-option"
              >
                {stock === 0 && `(${t('cartUi.giftsOutOfStock', { defaultValue: '贈品已贈完' })})`}
                {option.title}
              </SelectOption>
            )
          })}
        </Select>
      </Box>
    </HStack>
  )

  const renderCouponConditionText = () => (
    <>
      <CouponConditionPrefixIcon isApplied={isApplied} moreAmount={moreAmount} moreQuantity={moreQuantity} />
      <CouponConditionPrefixText isApplied={isApplied} moreAmount={moreAmount} moreQuantity={moreQuantity} currency={currency} />
      <CouponConditionLabel displayName={displayName} count={count} />
    </>
  )

  const renderContent = () => {
    if (isProgress) {
      return (
        <>
          {isMobile
            ? (
              <Tooltip title={displayName} variant="dark" placement="bottom" paperStyle={styles.tooltip}>
                <HStack justifyContent="space-between" alignItems="center" flexWrap="nowrap">
                  <HStack gap={2} flex={1} minWidth={0}>
                    {renderCouponConditionText()}
                  </HStack>
                  <ArrowRightSmall color={colors.grey600} />
                </HStack>
              </Tooltip>
            )
            : (
              <HStack gap={2}>
                {renderCouponConditionText()}
              </HStack>
            )}
          <HStack gap={2}>
            <Box width={16}>
              {!shouldHideProgressLine && <Box width={1} height="100%" margin="auto" backgroundColor={colors.grey300} />}
            </Box>
            <VStack gap={1} flex={1}>
              {renderSelectItem()}
            </VStack>
          </HStack>
        </>
      )
    }
    return (
      <>
        <CouponCondition
          displayName={displayName}
          isApplied={isApplied}
          currency={currency}
          moreAmount={moreAmount}
          moreQuantity={moreQuantity}
          count={count}
        />
        {renderSelectItem()}
      </>
    )
  }

  return (
    <VStack rootRef={rootRef} gap={2} backgroundColor={errorHighlight ? colors.red100 : undefined}>
      {renderContent()}
    </VStack>
  )
}
